<template>
  <v-row
    id="completed-order"
    class="completed-order"
  >
    <error-modal
      :open="partialFailure && !errorsDismissed"
      :failed-items="failedLineItems"
      failure-type="PARTIAL"
      @close="errorsDismissed = true"
    />

    <v-col
      cols="12"
      md="8"
      class="completed-order-wrapper"
    >
      <div class="completed-order-form">
        <div>
          <div id="checkmark" />
        </div>
        <div
          class="svp-text-dark"
          role="alert"
          tabindex="0"
        >
          <h1
            class="complete-thanks-heading"
            v-text="completeThanksHeadingI18nText"
          />
        </div>
      </div>

      <p
        class="complete-pending-processing"
        v-html="completeProcessingPending"
      />

      <survey-link v-if="hasRecipientSurveyEnabled" />

      <div
        v-if="hasLineItems"
        class="complete-order-reward-lists"
      >
        <div>
          <reward-lists
            :editable="false"
            :background-reversed="true"
            :hide-bottom-border="false"
            :item-groups="itemGroups"
            :is-direct-payment="isDirectPayment"
          />
        </div>
      </div>

      <div
        v-if="!isDirectPayment && hasLineItems"
        class="complete-order-total"
      >
        <div class="border-top border">
          <v-row>
            <v-col
              v-if="showTotalBox"
              cols="12"
              sm="6"
              class="border-sm-right text-center text-sm-right pt-3 pb-5 pr-3"
            >
              <span
                role="text"
                tabindex="0"
              >
                {{ $t('complete-total') }}
              </span>
              <span
                class="order-total svp-font-weight-bold pl-2 pr-2"
                tabindex="0"
              >
                {{ formattedOrderTotalCharged }}
              </span>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              class="pt-3 pb-5 text-center text-sm-left"
            >
              <div class="d-inline-block text-left pl-2">
                <div
                  v-if="hasSuccessfulPhysicalItems"
                  class="physical-address pb-3"
                >
                  <div
                    class="svp-font-weight-bold mb-2"
                    tabindex="0"
                  >
                    {{ $t('complete-physical-address-label') }}
                  </div>
                  <div
                    class="white-space-pre text-left"
                    tabindex="0"
                  >
                    {{ physicalDeliveryAddress | formatPhysicalAddress }}
                  </div>
                </div>

                <div
                  v-if="hasSuccessfulDigitalItems"
                  class="digital-address text-left"
                >
                  <div
                    class="svp-font-weight-bold mb-2"
                    tabindex="0"
                  >
                    {{ $t('complete-digital-address-label') }}
                  </div>
                  <div tabindex="0">
                    {{ digitalDeliveryAddress }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-row
        v-if="!isDirectPayment"
        class="complete-order-disclaimer"
        tabindex="0"
      >
        <v-col>
          <div
            v-if="hasSuccessfulDigitalItems"
            class="digital-disclaimer"
            v-text="$t('complete-disclaimer-digital')"
          />
          <div
            v-if="hasSuccessfulPhysicalItems"
            class="physical-disclaimer"
            v-text="$t('complete-disclaimer-physical')"
          />
        </v-col>
      </v-row>
      <need-help />
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import ErrorModal from '@/components/checkout/ErrorModal'
import RewardLists from '@/components/checkout/RewardLists.vue'
import SurveyLink from '@/components/shared/feedback/SurveyLink'
import NeedHelp from '@/components/shared/NeedHelp'
import { FulfillmentStatus, FulfillmentType } from '@/cowboy/EnumTypes'
import { CartItem, PhysicalRecipient } from '@/store/classes'
import { SVP_RL_1272_RECIPIENT_SURVEY } from '@/plugins/split/ActiveSplits'
import * as actionTypes from '@/store/action-types'
import * as getTypes from '@/store/get-types'
import { PAYMENT_REWARD_TYPES } from '@/components/shared/item/reward-type-constants'
import { OrderApi } from '@/cowboy/order/OrderApi'

export default {
  name: 'CompletedOrder',
  components: {
    SurveyLink,
    ErrorModal,
    RewardLists,
    NeedHelp
  },
  filters: {
    formatPhysicalAddress (physicalAddress) {
      const {
        streetLine1,
        streetLine2,
        city,
        stateOrProvince,
        postalCode,
        country
      } = physicalAddress

      const cityState = [city, stateOrProvince]
        .filter(item => !!item)
        .join(', ')
      const cityStateZip = [cityState, postalCode]
        .filter(item => !!item)
        .join(' ')

      return [streetLine1, streetLine2, cityStateZip, country]
        .filter(item => !!item)
        .join('\n')
    }
  },
  data () {
    return {
      errorsDismissed: false,
      pollingBackoffSeconds: 1,
      donePolling: false
    }
  },
  computed: {
    ...mapGetters({
      cartExternalRefId: getTypes.GET_CART_EXTERNAL_REF_ID,
      catalog: getTypes.GET_CATALOG,
      digitalRecipient: getTypes.GET_DIGITAL_RECIPIENT,
      orderConfirmation: getTypes.GET_ORDER_CONFIRMATION,
      physicalRecipient: getTypes.GET_PHYSICAL_RECIPIENT,
      rewardLinkEmail: getTypes.GET_REWARD_LINK_EMAIL
    }),
    partialFailure () {
      const { status } = this.orderConfirmation || {}
      return status === 'PARTIAL'
    },
    digitalDeliveryAddress () {
      const { email } = this.digitalRecipient
      return email
    },
    physicalDeliveryAddress () {
      return new PhysicalRecipient(this.physicalRecipient)
    },
    lineItems () {
      const { lineItems = [] } = this.orderConfirmation || {}
      return _.cloneDeep(lineItems).map(lineItem => ({
        ...lineItem,
        brand: this.findCatalogBrandForLineItem(lineItem)
      }))
    },
    successfulLineItems () {
      return this.lineItems.filter(
        lineItem =>
          lineItem.status === FulfillmentStatus.COMPLETE ||
          lineItem.status === FulfillmentStatus.PENDING
      )
    },
    successfulDigitalItems () {
      const { successfulLineItems } = this
      return successfulLineItems
        .filter(
          lineItem => lineItem.fulfillmentType === FulfillmentType.DIGITAL
        )
        .map(this.mapLineItem)
    },
    successfulPhysicalItems () {
      const { successfulLineItems } = this
      return successfulLineItems
        .filter(
          lineItem => lineItem.fulfillmentType === FulfillmentType.PHYSICAL
        )
        .map(this.mapLineItem)
    },
    hasRecipientSurveyEnabled () {
      return this.$isSplitEnabled(SVP_RL_1272_RECIPIENT_SURVEY)
    },
    hasSuccessfulDigitalItems () {
      return this.successfulDigitalItems.length > 0
    },
    hasSuccessfulPhysicalItems () {
      return this.successfulPhysicalItems.length > 0
    },
    completeThanksHeadingI18nText () {
      return this.isDirectPayment
        ? this.$t('complete-thanks-direct-payment-async')
        : this.$t('complete-thanks-async')
    },
    completeProcessingPending () {
      return this.isDirectPayment
        ? this.$t('complete-processing-pending-direct-payment', {
          emailAddress: this.rewardLinkEmail
        })
        : this.$t('complete-processing-pending')
    },
    failedLineItems () {
      return this.lineItems.filter(
        lineItem =>
          lineItem.status !== FulfillmentStatus.COMPLETE &&
          lineItem.status !== FulfillmentStatus.PENDING
      )
    },
    formattedOrderTotalCharged () {
      const {
        orderTotal: { currencyCode }
      } = this.orderConfirmation
      const totalCharged = this.successfulLineItems.reduce(
        (total, { amountCharged: { value, fee = 0 } }) => total + value + fee,
        0
      )
      return this.$money(totalCharged, currencyCode)
    },
    itemGroups () {
      const itemGroups = []

      if (this.hasSuccessfulPhysicalItems) {
        itemGroups.push({
          key: 'physical',
          label: this.$t('complete-physical-address-heading'),
          subLabel: this.$t('complete-physical-address-heading-note'),
          items: this.successfulPhysicalItems
        })
      }

      if (this.hasSuccessfulDigitalItems) {
        itemGroups.push({
          key: 'digital',
          label: this.$t('complete-digital-address-heading'),
          subLabel: this.$t('complete-digital-address-heading-note'),
          items: this.successfulDigitalItems
        })
      }

      if (this.failedLineItems.length) {
        itemGroups.push({
          key: 'failed',
          label: this.$t('order-failure-partial-list-header'),
          caption: this.$t('order-failure-partial-description'),
          items: this.failedLineItems.map(this.mapLineItem)
        })
      }
      return itemGroups
    },
    showTotalBox () {
      return this.containsMultipleItems
    },
    containsMultipleItems () {
      return (
        this.itemGroups.some(itemGroup => itemGroup.items.length > 1) ||
        this.itemGroups > 1
      )
    },
    hasLineItems () {
      return this.lineItems && !!this.lineItems.length
    },
    isDirectPayment () {
      return this.hasLineItems && this.lineItems.every(lineItem =>
        lineItem.brand.items.every(item =>
          PAYMENT_REWARD_TYPES.includes(item.rewardType.description)))
    }
  },
  mounted () {
    if (this.isDirectPayment) {
      document.title = `Disbursement Link | ${this.$t('complete-thanks-direct-payment-async')}`
    }
    this.pollOrderStatus()
  },
  methods: {
    ...mapActions({
      setOrderConfirmation: actionTypes.SET_ORDER_CONFIRMATION,
      fetchRewardLinkBalance: actionTypes.FETCH_REWARD_LINK_BALANCE
    }),
    pollOrderStatus () {
      if (
        !this.donePolling &&
        this.pollingBackoffSeconds < 2e8 &&
        this.orderConfirmation.status === FulfillmentStatus.PENDING &&
        this.$route.name === 'Complete' // poller will die after navigating away
      ) {
        this.setOrderStatus()
        this.pollingBackoffSeconds = 2 * this.pollingBackoffSeconds
        setTimeout(this.pollOrderStatus, 1000 * this.pollingBackoffSeconds)
      } else {
        // else, state is OK and no need to poll
        this.donePolling = true
      }
    },
    buildNumberCache (lineItems) {
      const numberCache = {}
      for (let i = 0; i < lineItems.length; i++) {
        if (lineItems[i].lineNumber !== undefined) {
          numberCache[lineItems[i].lineNumber] = i
        }
      }
      return numberCache
    },
    applyNumberCache (numberCache, mutableOrderConfirmation, lineItems) {
      for (const lineItem of lineItems) {
        if (lineItem.lineNumber !== undefined) {
          const lineNumber = lineItem.lineNumber
          if (numberCache[lineNumber] !== undefined) {
            mutableOrderConfirmation.lineItems[numberCache[lineNumber]].status =
              lineItem.status
          }
        }
      }
    },
    setStatusData (data) {
      const mutableOrderConfirmation = _.cloneDeep(this.orderConfirmation)
      if (data && data.lineItems) {
        const numberCache = this.buildNumberCache(
          this.orderConfirmation.lineItems
        )
        this.applyNumberCache(
          numberCache,
          mutableOrderConfirmation,
          data.lineItems
        )
      }
      if (
        data &&
        data.status &&
        data.status !== mutableOrderConfirmation.status
      ) {
        mutableOrderConfirmation.status = data.status
      }
      this.setOrderConfirmation(mutableOrderConfirmation)
      this.checkStatusesAndUpdateRewardLinkBalance()
    },
    async setOrderStatus () {
      try {
        const orderStatus = (await OrderApi.fetchOrderStatus(this.cartExternalRefId)).toJSON()
        this.setStatusData(orderStatus)
      } catch (e) {
        // 404 is expected; re-poll
        if (e.response.status === 404) {
          setTimeout(this.pollOrderStatus, 1000 * this.pollingBackoffSeconds)
        }
      }
    },
    findCatalogBrandForLineItem (lineItem) {
      return this.catalog.brands.find(({ items }) => {
        return items.find(
          item =>
            // prefer utid matching because it is a legit foreign key
            item.utid === lineItem.utid ||
            // ...as opposed to freeform text 😕
            item.rewardName === lineItem.rewardName
        )
      })
    },
    mapLineItem ({
      brand = {},
      lineItemTotal: { currencyCode } = {},
      fulfillmentType,
      status,
      utid,
      fee,
      amount,
      lineNumber
    }) {
      const {
        displayName: name,
        imageUrls: { '278w-326ppi': image } = {}
      } = brand
      return new CartItem(
        utid,
        null,
        amount,
        fee,
        brand,
        currencyCode,
        image,
        name,
        null,
        null,
        fulfillmentType,
        status,
        lineNumber
      )
    },
    async checkStatusesAndUpdateRewardLinkBalance () {
      const { status } = this.orderConfirmation
      if (status !== FulfillmentStatus.PENDING) {
        // if status is in anything other than PENDING,
        // fetch reward link and update availableAmount prop only
        await this.fetchRewardLinkBalance()
      }
    }
  }
}
</script>

<style lang="scss">
@import '../../assets/css/variables';

.completed-order-wrapper {
  display: inline-block;
  text-align: center;

  .complete-order-reward-lists {
    margin: 20px 0;
  }

  .complete-order-total {
    margin-bottom: 20px;
    color: $tangoTextPrimary;
  }

  .contact-options {
    margin-top: 30px;
  }
}

#completed-order {
  padding-top: 50px;
  display: block;
  text-align: center;

  .fade {
    &.in {
      opacity: 1;
    }
  }

  .d-inline-block {
    display: inline-block;
    color: $tangoTextPrimary;
  }

  .border-top {
    border-top: 2px solid $tangoLtGray;
  }

  .white-space-pre {
    white-space: pre-line;
  }

  #checkmark {
    display: inline-block;
    height: 65px;
    width: 65px;
    background-image: url('../../assets/images/checkmark.svg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: 35px auto;
    border: 3px solid #000000;
    border-radius: 50%;
  }

  .reference-order-id,
  .order-total {
    font-size: $font-size-md;
  }
  .items-group {
    padding-left: 25px !important;
    padding-right: 25px !important;

    .items-group-heading {
      text-align: left;
    }
  }
}

.complete-order-disclaimer {
  color: $tangoTextPrimary;
}

.border {
  border: solid 2px $tangoLtGray;
}

@media screen and (min-width: $breakpoint-md) {
  .border-sm-right {
    border-right: 2px solid $tangoLtGray;
  }
}
</style>
